import React, {useMemo} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {Review} from '@findhotel/sapi'
import {getLanguageCode} from 'modules/meta/selectors'
import {getIsTranslationAvailable} from 'utils/translations'

import {cssTheme} from '@vio/atlas/src/themes'

import {ReviewCarouselCard} from './ReviewCarouselCard'

interface Props {
  /** Array of reviews */
  reviews: Review[]
  /** Click event for read more and carousel card click */
  handleReadMoreClick?: () => void
  translatedReviews?: Record<string, string>
}

const ScrollableWrapper = styled.div`
  display: flex;
  width: 100%;
  overflow-x: auto;
  /* hide scrollbar */
  -ms-overflow-style: none; /* for Edge */
  scrollbar-width: none; /* for Firefox */
  &::-webkit-scrollbar {
    display: none; /* for Chrome, Safari and Opera */
  }
  overflow-scrolling: touch;
  scroll-snap-type: x mandatory;
  gap: ${cssTheme.layout.spacing.s300};
  padding: 0 ${cssTheme.layout.spacing.s500};
`

const SlideWrapper = styled.div`
  flex-shrink: 0;
  width: calc(100% - 44px);
  scroll-snap-align: center;
  scroll-snap-stop: always;
  position: relative;
`

export const ReviewCarousel = ({
  reviews,
  handleReadMoreClick,
  translatedReviews = {}
}: Props) => {
  const languageCode = useSelector(getLanguageCode)

  const hasTranslationButton: boolean = useMemo(() => {
    return reviews.some(review =>
      getIsTranslationAvailable(review.language, languageCode)
    )
  }, [reviews, languageCode])

  return (
    <ScrollableWrapper data-id="hotelReviewsCarousel">
      {reviews.map((review, index) => (
        <SlideWrapper
          key={`${index}-${review.reviewerScore}-${review.reviewDate}`}
          data-id="hotelReviewsCarouselItem"
        >
          <ReviewCarouselCard
            review={review}
            translatedReviews={translatedReviews}
            onReadMoreClick={handleReadMoreClick}
            hasTranslationButton={hasTranslationButton}
          />
        </SlideWrapper>
      ))}
    </ScrollableWrapper>
  )
}
